"use client";
import { useCompositeScopedContext } from "./composite-context.js";
import { flipItems } from "./utils.js";
import { createElement, createHook, forwardRef, useEvent } from "@ariakit/react-utils";
import { getDocument, invariant, isSelfTarget, isTextField, normalizeString, removeUndefinedValues, sortBasedOnDOMPosition } from "@ariakit/utils";
//#region src/composite/composite-typeahead.tsx
const TagName = "div";
const typeaheadStates = /* @__PURE__ */ new WeakMap();
function getTypeaheadState(store) {
	let typeaheadState = typeaheadStates.get(store);
	if (!typeaheadState) {
		typeaheadState = {
			chars: "",
			cleanupTimeout: 0
		};
		typeaheadStates.set(store, typeaheadState);
	}
	return typeaheadState;
}
function clearChars(typeaheadState) {
	typeaheadState.chars = "";
}
function isValidTypeaheadEvent(event, typeaheadState) {
	const target = event.target;
	if (target && isTextField(target)) return false;
	if (event.key === " " && typeaheadState.chars.length) return true;
	return event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey && /^[\p{Letter}\p{Number}]$/u.test(event.key);
}
function isSelfTargetOrItem(event, items) {
	if (isSelfTarget(event)) return true;
	const target = event.target;
	if (!target) return false;
	return items.some((item) => item.element === target);
}
function getEnabledItems(items) {
	return items.filter((item) => !item.disabled);
}
function itemTextStartsWith(item, text) {
	const itemText = item.element?.textContent || item.children || "value" in item && item.value;
	if (!itemText) return false;
	return normalizeString(itemText).trim().toLowerCase().startsWith(text.toLowerCase());
}
function getSameInitialItems({ activeId, char, items, typeaheadState }) {
	if (!activeId) return items;
	const activeItem = items.find((item) => item.id === activeId);
	if (!activeItem) return items;
	if (!itemTextStartsWith(activeItem, char)) return items;
	const { chars } = typeaheadState;
	if (chars !== char && itemTextStartsWith(activeItem, chars)) return items;
	typeaheadState.chars = char;
	return flipItems(items.filter((item) => itemTextStartsWith(item, char)), activeId).filter((item) => item.id !== activeId);
}
/**
* Returns props to create a `CompositeTypeahead` component.
* @see https://ariakit.com/components/composite
* @example
* ```jsx
* const store = useCompositeStore();
* const props = useCompositeTypeahead({ store });
* <Composite store={store} {...props}>
*   <CompositeItem>Item 1</CompositeItem>
*   <CompositeItem>Item 2</CompositeItem>
* </Composite>
* ```
*/
const useCompositeTypeahead = createHook(function useCompositeTypeahead({ store, typeahead = true, ...props }) {
	const context = useCompositeScopedContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "CompositeTypeahead must be a Composite component");
	const onKeyDownCaptureProp = props.onKeyDownCapture;
	const onKeyDownCapture = useEvent((event) => {
		onKeyDownCaptureProp?.(event);
		if (event.defaultPrevented) return;
		if (!typeahead) return;
		if (!store) return;
		const typeaheadState = getTypeaheadState(store);
		if (!isValidTypeaheadEvent(event, typeaheadState)) return clearChars(typeaheadState);
		const { renderedItems, items, activeId, id } = store.getState();
		let enabledItems = getEnabledItems(items.length > renderedItems.length ? items : renderedItems);
		const document = getDocument(event.currentTarget);
		const selector = `[data-offscreen-id="${id}"]`;
		const offscreenItems = document.querySelectorAll(selector);
		for (const element of offscreenItems) {
			if (element.hasAttribute("data-disabled")) continue;
			if ("disabled" in element && !!element.disabled) continue;
			enabledItems.push({
				id: element.id,
				element
			});
		}
		if (offscreenItems.length) enabledItems = sortBasedOnDOMPosition(enabledItems, (i) => i.element);
		if (!isSelfTargetOrItem(event, enabledItems)) return clearChars(typeaheadState);
		event.preventDefault();
		window.clearTimeout(typeaheadState.cleanupTimeout);
		typeaheadState.cleanupTimeout = window.setTimeout(() => {
			clearChars(typeaheadState);
		}, 500);
		const char = event.key.toLowerCase();
		typeaheadState.chars += char;
		enabledItems = getSameInitialItems({
			activeId,
			char,
			items: enabledItems,
			typeaheadState
		});
		const item = enabledItems.find((item) => itemTextStartsWith(item, typeaheadState.chars));
		if (item) store.move(item.id);
		else clearChars(typeaheadState);
	});
	props = {
		...props,
		onKeyDownCapture
	};
	return removeUndefinedValues(props);
});
/**
* Renders a component that adds typeahead functionality to composite
* components.
*
* When the
* [`typeahead`](https://ariakit.com/reference/composite-typeahead#typeahead)
* prop is enabled, which it is by default, hitting printable character keys
* will move focus to the next composite item that begins with the input
* characters.
* @see https://ariakit.com/components/composite
* @example
* ```jsx
* <CompositeProvider>
*   <Composite render={<CompositeTypeahead />}>
*     <CompositeItem>Item 1</CompositeItem>
*     <CompositeItem>Item 2</CompositeItem>
*   </Composite>
* </CompositeProvider>
* ```
*/
const CompositeTypeahead = forwardRef(function CompositeTypeahead(props) {
	return createElement(TagName, useCompositeTypeahead(props));
});
//#endregion
export { CompositeTypeahead, useCompositeTypeahead };

//# sourceMappingURL=composite-typeahead.js.map